/**
 * The connection dialog's geometry (Option C, C1, plan §3.5). Pure: every number is in CSS px, which already include the
 * UI scale (a 1024-px window at scale 1.25 is an 819-px viewport).
 *
 * Create mode: two panes (rail 220 px + form) from a 936-px viewport, with `W = min(900, 100vw − 10rem)` including the
 * dialog's 2 px border, so the form content is `F = W − 2 − 220 − 56 = W − 278` (498 px at 936). Below that, one column
 * under the compact bar: `W = 100vw − 32`, `F = W − 58`. Edit mode: 720 px (or `100vw − 32` below 752), no rail.
 * Labels sit left of their fields (C2) when the form content is at least 31 rem (496 px).
 *
 * Height: the short tier starts below a 560-px viewport (dialog `max-h = 100dvh − 16px`). The very-short tier is
 * MEASURED: when the fixed parts would leave the form region under 96 px, only the header and footer stay fixed.
 */
export const TWO_PANE_MIN_VIEWPORT = 936;
export const LABEL_LEFT_MIN_CONTENT = 496;
/** The short tier applies below this viewport height. */
export const SHORT_TIER_VIEWPORT_HEIGHT = 560;
export const SHORT_FORM_MIN_HEIGHT = 96;
export const DIALOG_MAX_WIDTH = 900;
export const EDIT_DIALOG_WIDTH = 720;
/** The dialog's 1 px border on each side. */
export const DIALOG_BORDER = 2;
/** The short tier's dialog keeps 8 px of viewport above and below. */
export const SHORT_TIER_MARGIN = 16;

export type DialogMode = 'create' | 'edit';
export type DialogColumns = 'two-pane' | 'one-column';
export type LabelPlacement = 'left' | 'above';

export type DialogGeometry = {
  columns: DialogColumns;
  /** `W`, border included. */
  dialogWidth: number;
  /** `F`, the form content box inside the pane padding and scrollbar gutter. */
  formContentWidth: number;
  labels: LabelPlacement;
  /** The short tier (viewport height under 560 px). */
  short: boolean;
};

export type Viewport = { width: number; height: number };

export function dialogGeometry({ width, height }: Viewport, mode: DialogMode): DialogGeometry {
  let columns: DialogColumns = 'one-column';
  let dialogWidth: number;
  let formContentWidth: number;
  if (mode === 'edit') {
    dialogWidth = width < EDIT_DIALOG_WIDTH + 32 ? width - 32 : EDIT_DIALOG_WIDTH;
    formContentWidth = dialogWidth - 58;
  } else if (width >= TWO_PANE_MIN_VIEWPORT) {
    columns = 'two-pane';
    dialogWidth = Math.min(DIALOG_MAX_WIDTH, width - 160);
    formContentWidth = dialogWidth - 278;
  } else {
    dialogWidth = width - 32;
    formContentWidth = dialogWidth - 58;
  }
  return {
    columns,
    dialogWidth,
    formContentWidth,
    labels: formContentWidth >= LABEL_LEFT_MIN_CONTENT ? 'left' : 'above',
    short: height < SHORT_TIER_VIEWPORT_HEIGHT,
  };
}

/**
 * The very-short tier: in the short tier, the fixed parts (border, header, compact bar, pane header, result summary,
 * footer — `fixedHeight`) would leave the form region under 96 px of the dialog's `100dvh − 16px`.
 */
export function isVeryShort(viewportHeight: number, fixedHeight: number): boolean {
  if (viewportHeight >= SHORT_TIER_VIEWPORT_HEIGHT) return false;
  return viewportHeight - SHORT_TIER_MARGIN - fixedHeight < SHORT_FORM_MIN_HEIGHT;
}
